import { Service, ServiceSellPrice } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { Button, NumberInput, Loader, Badge, Switch } from '@mantine/core';
import { IconClock, IconAlertCircle } from '@tabler/icons-react';
import { ServiceTitleStatic } from '../services/service-title';
import DatePickerCustom from '../custom-date-picker';
import { DEFAULT_CURRENCY } from '@turizi/shared';
import { formatDayLong, formatPrice } from '@/lib/format-utils';
import { useState } from 'react';
import { useUpdateServiceSellPrice } from '@/hooks/api-hooks';
import { notify } from '@/lib/notify';

interface PricesTabProps {
    services: Service[];
}

interface ServiceUpdateStatus {
    id: Service['id'];
    loading: boolean;
    success: boolean | null;
}

const DELAY = 350; // ms

export function BulkModifyPricesTab({ services }: PricesTabProps) {
    const { t } = useTranslation();
    const updatePriceMutation = useUpdateServiceSellPrice();

    const [startDay, setStartDay] = useState<string>('');
    const [endDay, setEndDay] = useState<string>('');
    const [priceFactor, setPriceFactor] = useState<number>(1);
    const [applyToLinkedService, setApplyToLinkedService] = useState<boolean>(false);
    const [updateStatuses, setUpdateStatuses] = useState<Record<Service['id'], ServiceUpdateStatus>>({});
    const [counter, setCounter] = useState<number>(0);

    const hasStarted = Object.keys(updateStatuses).length > 0;
    const allCompleted = hasStarted && Object.values(updateStatuses).every(status => !status.loading);

    const getActivePrice = (service: Service): ServiceSellPrice | null => {
        const activePrices = service?.sell_prices?.filter(p => p.status === 'active');
        if (activePrices && activePrices.length > 0) {
            return activePrices[activePrices.length - 1];
        }
        if (service?.sell_prices && service.sell_prices.length > 0) {
            return service.sell_prices[service.sell_prices.length - 1];
        }
        return null;
    };

    const handleBulkModifyPrice = async () => {
        const isFactorModified = priceFactor !== 1;
        const isStartDayModified = Boolean(startDay);
        const isEndDayModified = Boolean(endDay);

        if (!isFactorModified && !isStartDayModified && !isEndDayModified) {
            notify.error(t('error'), t('no_changes_to_apply'));
            return;
        }

        const servicesToUpdate = services.filter(service => getActivePrice(service) !== null);

        if (servicesToUpdate.length === 0) {
            notify.error(t('error'), t('no_services_with_active_price'));
            return;
        }

        const initialStatuses: Record<Service['id'], ServiceUpdateStatus> = {};
        services.forEach(service => {
            const hasPrice = getActivePrice(service) !== null;
            initialStatuses[service.id] = {
                id: service.id,
                loading: hasPrice,
                success: hasPrice ? null : false,
            };
        });
        setUpdateStatuses(initialStatuses);
        setCounter(0);

        let completed = 0;
        for (let i = 0; i < servicesToUpdate.length; i++) {
            const service = servicesToUpdate[i];

            if (i > 0) {
                await new Promise(resolve => setTimeout(resolve, DELAY));
            }

            const currentPrice = getActivePrice(service)!;

            const priceData: Partial<ServiceSellPrice> & { apply_to_linked_service?: boolean } = {
                id: currentPrice.id,
                service_id: service.id,
                apply_to_linked_service: applyToLinkedService,
            };

            if (isStartDayModified) {
                priceData.start_day = startDay;
            }

            if (isEndDayModified) {
                priceData.end_day = endDay;
            }

            if (isFactorModified) {
                priceData.default_amount = (currentPrice.default_amount || 0) * priceFactor;

                if (currentPrice.child_amount !== null && currentPrice.child_amount !== undefined) {
                    priceData.child_amount = currentPrice.child_amount * priceFactor;
                }

                if (currentPrice.senior_amount !== null && currentPrice.senior_amount !== undefined) {
                    priceData.senior_amount = currentPrice.senior_amount * priceFactor;
                }

                if (currentPrice.infant_amount !== null && currentPrice.infant_amount !== undefined) {
                    priceData.infant_amount = currentPrice.infant_amount * priceFactor;
                }
            }

            try {
                await updatePriceMutation.mutateAsync(priceData);
                completed++;
                setCounter(completed);
                setUpdateStatuses(prev => ({
                    ...prev,
                    [service.id]: { id: service.id, loading: false, success: true }
                }));
            } catch (error) {
                completed++;
                setCounter(completed);
                setUpdateStatuses(prev => ({
                    ...prev,
                    [service.id]: { id: service.id, loading: false, success: false }
                }));
            }
        }
    };

    return (
        <>
            <div className="space-y-4">
                <div className="flex gap-4">
                    <DatePickerCustom
                        label={t('start_date')}
                        placeholder={t('keep_current_date')}
                        value={startDay}
                        onChange={(val: any) => {
                            const d = Array.isArray(val) ? (val[0] || val[1] || '') : (val || '');
                            setStartDay(d);
                        }}
                        clearable={true}
                        className="w-1/2"
                    />

                    <DatePickerCustom
                        label={t('end_date')}
                        placeholder={t('keep_current_date')}
                        value={endDay}
                        onChange={(val: any) => {
                            const d = Array.isArray(val) ? (val[0] || val[1] || '') : (val || '');
                            setEndDay(d);
                        }}
                        clearable={true}
                        className="w-1/2"
                    />
                </div>

                <NumberInput
                    label={t('price_factor')}
                    description={t('multiply_current_price_by_factor')}
                    value={priceFactor}
                    onChange={(value) => setPriceFactor(Number(value))}
                    step={0.1}
                    min={0.1}
                    decimalScale={2}
                />

                <Switch
                    label={t('also_update_linked_service')}
                    checked={applyToLinkedService}
                    onChange={(e) => setApplyToLinkedService(e.currentTarget.checked)}
                    color="black"
                />

                <div className="text-sm font-medium mb-2">
                    {t('services_with_modified_prices')}
                </div>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                    {services?.map(service => {
                        const currentPrice = getActivePrice(service);

                        if (!currentPrice) {
                            return (
                                <div key={service.id} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <IconClock size={16} className="text-amber-500" />
                                            <span className="text-sm font-medium text-gray-700">
                                                {ServiceTitleStatic(service)}
                                            </span>
                                        </div>
                                        <Badge color="amber" variant="light" size="sm" leftSection={<IconAlertCircle size={12} />}>
                                            {t('no_active_price_found')}
                                        </Badge>
                                    </div>
                                </div>
                            );
                        }

                        const currency = currentPrice.default_currency || DEFAULT_CURRENCY;

                        const displayStartDay = startDay || currentPrice.start_day;
                        const displayEndDay = endDay || currentPrice.end_day;

                        const new_default_amount = (currentPrice.default_amount || 0) * priceFactor;
                        const new_child_amount = currentPrice.child_amount !== null && currentPrice.child_amount !== undefined
                            ? currentPrice.child_amount * priceFactor
                            : null;
                        const new_senior_amount = currentPrice.senior_amount !== null && currentPrice.senior_amount !== undefined
                            ? currentPrice.senior_amount * priceFactor
                            : null;
                        const new_infant_amount = currentPrice.infant_amount !== null && currentPrice.infant_amount !== undefined
                            ? currentPrice.infant_amount * priceFactor
                            : null;

                        const datesChanged = Boolean(startDay || endDay);
                        const factorChanged = priceFactor !== 1;

                        return (
                            <div key={service.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex items-center gap-2 mb-3">
                                    <IconClock size={16} className="text-gray-500" />
                                    <span className="text-sm font-medium text-gray-700">
                                        {ServiceTitleStatic(service)}
                                    </span>
                                </div>

                                <div className="text-xs text-gray-500 mb-2">
                                    {t('current_price')}: <span className="font-semibold">{formatDayLong(currentPrice.start_day)} - {formatDayLong(currentPrice.end_day)}</span>
                                    {datesChanged && (
                                        <span className="ml-2 text-blue-600 font-semibold">
                                            ({t('modified_price')}: {formatDayLong(displayStartDay)} - {formatDayLong(displayEndDay)})
                                        </span>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    {/* Adults / Default */}
                                    <div className="flex items-center justify-between text-xs bg-blue-50 p-2 rounded">
                                        <span className="font-medium text-gray-600">{t('adults')}</span>
                                        <div className="flex items-center gap-2">
                                            <span className="text-gray-600">{formatPrice(currentPrice.default_amount || 0)} {currency}</span>
                                            {factorChanged && (
                                                <>
                                                    <span className="text-gray-400">→</span>
                                                    <span className="font-bold text-green-600">{formatPrice(new_default_amount)} {currency}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Children */}
                                    {currentPrice.child_amount !== null && currentPrice.child_amount !== undefined && (
                                        <div className="flex items-center justify-between text-xs bg-white p-2 rounded">
                                            <span className="font-medium text-gray-600">{t('children')}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-600">{formatPrice(currentPrice.child_amount)} {currency}</span>
                                                {factorChanged && new_child_amount !== null && (
                                                    <>
                                                        <span className="text-gray-400">→</span>
                                                        <span className="font-bold text-green-600">{formatPrice(new_child_amount)} {currency}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Seniors */}
                                    {currentPrice.senior_amount !== null && currentPrice.senior_amount !== undefined && (
                                        <div className="flex items-center justify-between text-xs bg-white p-2 rounded">
                                            <span className="font-medium text-gray-600">{service.show_retired ? t('retired_plural') : t('seniors')}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-600">{formatPrice(currentPrice.senior_amount)} {currency}</span>
                                                {factorChanged && new_senior_amount !== null && (
                                                    <>
                                                        <span className="text-gray-400">→</span>
                                                        <span className="font-bold text-green-600">{formatPrice(new_senior_amount)} {currency}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Infants */}
                                    {currentPrice.infant_amount !== null && currentPrice.infant_amount !== undefined && (
                                        <div className="flex items-center justify-between text-xs bg-white p-2 rounded">
                                            <span className="font-medium text-gray-600">{t('infants')}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-gray-600">{formatPrice(currentPrice.infant_amount)} {currency}</span>
                                                {factorChanged && new_infant_amount !== null && (
                                                    <>
                                                        <span className="text-gray-400">→</span>
                                                        <span className="font-bold text-green-600">{formatPrice(new_infant_amount)} {currency}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Footer */}
            <div className="mt-8">
                {!hasStarted ? (
                    <div className="flex justify-end">
                        <Button
                            onClick={handleBulkModifyPrice}
                            color="black"
                            radius="xl"
                            size="xs"
                        >
                            {t('apply_changes')}
                        </Button>
                    </div>
                ) : allCompleted ? (
                    <div className="flex items-center justify-center gap-2">
                        <div className="text-sm font-medium text-green-600">
                            {t('done')}
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-2">
                        <div className="text-sm font-medium">
                            {t('updating')} {counter} / {services.length}
                        </div>
                        <Loader size="sm" color="black" />
                    </div>
                )}
            </div>
        </>
    );
}
